
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { useIsMobile } from '@/hooks/use-mobile';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, Clock, Users, MapPin } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { coachServiceFixed as coachService } from '@/services/coachServiceFixed';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import CreateEventModal from '@/components/schedule/CreateEventModal';
import { ParticipantManager } from '@/components/schedule/ParticipantManager';
import { VenueGameRosterModal } from '@/components/games/VenueGameRosterModal';
import { gameRosterService } from '@/services/gameRosterService';
import { toast } from 'sonner';

const CoachSchedule = () => {
  const { user: supabaseUser, profile } = useAuth();
  const { user: internalUser, profile: internalProfile } = useInternalAuth();

  const user = supabaseUser || internalUser;
  const userProfile = profile || internalProfile;
  const currentUserId = profile?.id || internalUser?.id;
  const isInternalUser = !!internalUser && !supabaseUser;
  const isMobile = useIsMobile();

  // Redirect if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const getDisplayName = () => {
    if (isInternalUser && internalUser) {
      return internalProfile?.first_name || internalUser.username;
    }
    return profile?.first_name || 
           supabaseUser?.user_metadata?.first_name || 
           supabaseUser?.user_metadata?.username || 
           'Coach';
  };

  const [scheduleItems, setScheduleItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showParticipantModal, setShowParticipantModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<{ id: string; teamId?: number | null; eventType?: string; gameCode?: string; resolvedGameId?: string } | null>(null);
  const [analytics, setAnalytics] = useState<any>({
    thisWeekEvents: 0,
    totalHours: 0,
    totalParticipants: 0
  });

  const loadSchedule = async () => {
    if (!currentUserId) return;
    
    try {
      setLoading(true);
      // Note: Schedule and analytics functionality not yet implemented in coachServiceFixed
      const schedule: any[] = [];
      const analyticsData = { thisWeekEvents: 0, totalHours: 0, totalParticipants: 0 };
      setScheduleItems(schedule);
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Error loading schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSchedule();
  }, [currentUserId]);

  const getTypeColor = (type: string) => {
    const colors = {
      'practice': 'bg-blue-600/20 text-blue-400 border-blue-600/30',
      'game': 'bg-sports-green/20 text-sports-green border-sports-green/30',
      'meeting': 'bg-purple-600/20 text-purple-400 border-purple-600/30',
      'training': 'bg-orange-600/20 text-orange-400 border-orange-600/30'
    };
    return colors[type] || colors['practice'];
  };

  const formatEndTime = (startTime: string, eventType: string) => {
    if (eventType === 'game') {
      // Add 2 hours to game start time for estimated end time
      const [hours, minutes] = startTime.split(':');
      const startDate = new Date();
      startDate.setHours(parseInt(hours), parseInt(minutes));
      startDate.setHours(startDate.getHours() + 2);
      return startDate.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      }) + ' CST';
    }
    return startTime;
  };

  const formatStartTime = (time: string) => {
    try {
      const [hours, minutes] = time.split(':').map(Number);
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);
      
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      }) + ' CST';
    } catch {
      return time + ' CST';
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-black">
      {!isMobile && <Header />}
      {isMobile && <MobileHeader />}
      
      <main className="flex-grow mt-32 py-6 md:py-12 px-4 md:px-6 pb-20 md:pb-6">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-3xl md:text-4xl font-bold text-white">
                {getDisplayName()}'s Schedule
              </h1>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-sports-green text-black hover:bg-green-600"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Event
              </Button>
            </div>
            <p className="text-gray-400">
              Manage your coaching schedule and upcoming events
            </p>
          </div>

          {/* Schedule Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-[#0d1117] border border-gray-800">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-400">This Week</CardTitle>
                <Calendar className="h-4 w-4 text-sports-green" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">{analytics.thisWeekEvents}</div>
                <p className="text-xs text-gray-400">Scheduled events</p>
              </CardContent>
            </Card>

            <Card className="bg-[#0d1117] border border-gray-800">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-400">Total Hours</CardTitle>
                <Clock className="h-4 w-4 text-sports-green" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">{analytics.totalHours}</div>
                <p className="text-xs text-gray-400">This week</p>
              </CardContent>
            </Card>

            <Card className="bg-[#0d1117] border border-gray-800">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-400">Participants</CardTitle>
                <Users className="h-4 w-4 text-sports-green" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">{analytics.totalParticipants}</div>
                <p className="text-xs text-gray-400">Total expected</p>
              </CardContent>
            </Card>
          </div>

          {/* Schedule Items */}
          <Card className="bg-[#0d1117] border border-gray-800">
            <CardHeader>
              <CardTitle className="text-sports-green">Upcoming Events</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {loading ? (
                  <div className="text-center py-8">
                    <p className="text-gray-400">Loading schedule...</p>
                  </div>
                ) : scheduleItems.length > 0 ? (
                  scheduleItems.map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-white font-medium">{item.title}</h3>
                        <span className={`px-2 py-1 rounded text-xs ${getTypeColor(item.event_type)}`}>
                          {item.event_type}
                        </span>
                        <span className={`px-2 py-1 rounded text-xs border ${
                          item.status === 'scheduled' ? 'bg-blue-600/20 text-blue-400 border-blue-600/30' :
                          item.status === 'completed' ? 'bg-green-600/20 text-green-400 border-green-600/30' :
                          item.status === 'cancelled' ? 'bg-red-600/20 text-red-400 border-red-600/30' :
                          'bg-yellow-600/20 text-yellow-400 border-yellow-600/30'
                        }`}>
                          {item.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-400">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {new Date(item.event_date).toLocaleDateString()}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {formatStartTime(item.start_time)} - {item.end_time ? formatStartTime(item.end_time) : formatEndTime(item.start_time, item.event_type)}
                        </div>
                        {item.location && (
                          <div className="flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            {item.location}
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <Users className="h-4 w-4" />
                          {item.participant_count} participants
                        </div>
                      </div>
                      <div className="mt-3 flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            const gameCode = item.description?.includes('Game Code:') ? item.description.split('Game Code: ')[1]?.trim() : undefined;
                            
                            if (item.event_type === 'game' && gameCode) {
                              // Resolve game code to actual game ID for venue games
                              try {
                                const resolvedGameId = await gameRosterService.resolveGameCodeToId(gameCode);
                                if (resolvedGameId) {
                                  setSelectedEvent({ 
                                    id: item.id, 
                                    teamId: item.team_id, 
                                    eventType: item.event_type,
                                    gameCode,
                                    resolvedGameId
                                  });
                                } else {
                                  toast.error('Could not find game with code: ' + gameCode);
                                  return;
                                }
                              } catch (error) {
                                toast.error('Error resolving game code');
                                console.error('Error resolving game code:', error);
                                return;
                              }
                            } else {
                              setSelectedEvent({ 
                                id: item.id, 
                                teamId: item.team_id, 
                                eventType: item.event_type,
                                gameCode
                              });
                            }
                            setShowParticipantModal(true);
                          }}
                          className="text-xs"
                        >
                          <Users className="h-3 w-3 mr-1" />
                          {item.event_type === 'game' ? 'Manage Game Roster' : 'Manage Participants'}
                        </Button>
                      </div>
                      {item.description && (
                        <p className="text-sm text-gray-500 mt-2">{item.description}</p>
                      )}
                    </div>
                  </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <Calendar className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                    <p className="text-gray-400 mb-4">No scheduled events yet.</p>
                    <p className="text-sm text-gray-500">
                      Events will appear here as you schedule practices, games, and meetings.
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

        </div>
      </main>
      
      <CreateEventModal 
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onEventCreated={loadSchedule}
      />
      
      {selectedEvent && selectedEvent.eventType === 'game' ? (
        <VenueGameRosterModal
          open={showParticipantModal}
          onClose={() => {
            setShowParticipantModal(false);
            setSelectedEvent(null);
          }}
          gameId={selectedEvent.resolvedGameId || selectedEvent.id}
          gameCode={selectedEvent.gameCode}
          onRosterUpdate={loadSchedule}
        />
      ) : selectedEvent && (
        <ParticipantManager
          open={showParticipantModal}
          onClose={() => {
            setShowParticipantModal(false);
            setSelectedEvent(null);
          }}
          eventId={selectedEvent.id}
          teamId={selectedEvent.teamId}
          onParticipantUpdate={loadSchedule}
        />
      )}
      
      <Footer />
      <MobileBottomNav />
    </div>
  );
};

export default CoachSchedule;
